import * as React from 'react';
import classnames from 'classnames';
import { createRecordUrl, formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { RechnungPosition, defaultRechnungPosition, moveUpRechnungPosition } from 'api/rechnungPosition';
import { EditIcon, DeleteIcon, UpIcon } from '@fluentui/react-icons';
import { Kunde } from 'api/kunde';
import { formStyles } from 'styles/formStyles';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { getArtikelDetailId } from 'utils/helper';
import { RechnungPositionEdit } from './RechnungPositionEdit';
import { RechnungPositionDelete } from './RechnungPositionDelete';
import { RechnungPositionenHeader } from './RechnungPositionenHeader';

interface RechnungPositionenProps {
    kunde: Kunde;
    rNr: string;
    rechnungPositionen: RechnungPosition[];
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    allowEdit?: boolean;
    refetch?: () => void;
}

export const RechnungPositionen: React.FC<RechnungPositionenProps> = ({
    kunde,
    rNr,
    rechnungPositionen,
    withHeader = true,
    allowEdit = false,
    refetch,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    const [rechnungPositionEditData, setRechnungPositionEditData] = React.useState<RechnungPosition | null>(null);
    const [rechnungPositionDeleteData, setRechnungPositionDeleteData] = React.useState<RechnungPosition | null>(null);

    return (
        <>
            <table className={formStyles.positionenTable}>
                {withHeader && (
                    <RechnungPositionenHeader
                        allowEdit={allowEdit}
                        onAdd={() =>
                            setRechnungPositionEditData({
                                ...defaultRechnungPosition,
                                rNr,
                                pos: -1,
                            })
                        }
                    />
                )}
                <tbody>
                    {rechnungPositionen.map((rechnungPosition) => (
                        <tr key={rechnungPosition.pos} className={formStyles.positionenRow}>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenNrCell)}>
                                {rechnungPosition.pos}
                            </td>
                            <td
                                className={classnames(
                                    formStyles.positionenCell,
                                    formStyles.positionenArtikelGruppeCell,
                                )}
                            >
                                {(artikelGruppenByNr[rechnungPosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[rechnungPosition.artikelGruppeNr]?.text) ||
                                    rechnungPosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenArtikelNrCell)}>
                                <span>
                                    {rechnungPosition.artikelGruppeNr && rechnungPosition.artikelNr ? (
                                        <OpenLink
                                            link={createRecordUrl(
                                                'lager/artikel',
                                                undefined,
                                                getArtikelDetailId(
                                                    rechnungPosition.artikelGruppeNr,
                                                    rechnungPosition.artikelNr,
                                                ),
                                                undefined,
                                                false,
                                            )}
                                            label={rechnungPosition.artikelNr}
                                        />
                                    ) : (
                                        ''
                                    )}
                                </span>
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenTextCell)}>
                                {rechnungPosition.posText}
                                {rechnungPosition.posBem ? (
                                    <i>
                                        <br />
                                        {rechnungPosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenEinhCell)}>
                                {rechnungPosition.posEinh
                                    ? einheitenByCode[rechnungPosition.posEinh].text || rechnungPosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(rechnungPosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatDecimal(rechnungPosition.posAMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(rechnungPosition.posEkPreis, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(rechnungPosition.posEPreis, 3)}
                            </td>
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}
                                title={'VK-Brutto: ' + formatMoney(rechnungPosition.posBrutto)}
                            >
                                {formatMoney(rechnungPosition.posNetto)}
                            </td>
                            {/*
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                {formatMoney(rechnungPosition.posBrutto)}
                            </td>
                            */}
                            <td
                                className={classnames(formStyles.positionenCell, formStyles.positionenMwstCell)}
                                title={'VK-Brutto: ' + formatMoney(rechnungPosition.posBrutto)}
                            >
                                {formatPercent(rechnungPosition.posMwst)}
                            </td>
                            {allowEdit && (
                                <>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        {rechnungPosition.pos > 1 && (
                                            <UpIcon
                                                className={formStyles.positionenAction}
                                                onClick={async () => {
                                                    await moveUpRechnungPosition(
                                                        rechnungPosition.rNr,
                                                        rechnungPosition.pos,
                                                    );
                                                    if (refetch) {
                                                        refetch();
                                                    }
                                                }}
                                            />
                                        )}
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        <EditIcon
                                            className={formStyles.positionenAction}
                                            onClick={() => setRechnungPositionEditData(rechnungPosition)}
                                        />
                                    </td>
                                    <td
                                        className={classnames(
                                            formStyles.positionenCell,
                                            formStyles.positionenActionCell,
                                        )}
                                    >
                                        <DeleteIcon
                                            className={formStyles.positionenAction}
                                            onClick={() => setRechnungPositionDeleteData(rechnungPosition)}
                                        />
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            {rechnungPositionEditData !== null && (
                <RechnungPositionEdit
                    kunde={kunde}
                    rNr={rechnungPositionEditData.rNr}
                    pos={rechnungPositionEditData.pos}
                    onClose={(_refetch) => {
                        setRechnungPositionEditData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
            {rechnungPositionDeleteData !== null && (
                <RechnungPositionDelete
                    rechnungPosition={rechnungPositionDeleteData}
                    onClose={(_refetch) => {
                        setRechnungPositionDeleteData(null);
                        if (_refetch && refetch) {
                            refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
